import {
  BaseError,
  createErrorBuilder,
} from '@wix/public-editor-platform-errors';

import { PrivateAPINamespace } from '../PrivateAPINamespace';

interface IToken {
  id?: string;
  value?: string;
  expires?: Date;
}

interface ICreateOTTokenRequest {
  tokenMetadata: IOTTokenMetadata;
}

interface IOTTokenMetadata {
  siteId?: string;
  appId?: string;
  originDomain?: string | null;
}
export interface ICreateOTTokenResponse {
  tokenResult?: IOTTokenResult;
}

export interface IOTTokenResult {
  token?: IToken;
  tokenMetadata?: IOTTokenMetadata;
  error?: string | null;
}

interface ICreateTokenResponse {
  token?: IToken;
}

interface ICreateTokenRequest {
  otp?: string;
}

enum AuthNamespaceErrorCode {
  TokenRequestFailed = 'TokenRequestFailed',
  OTTokenRequestFailed = 'OTTokenRequestFailed',
}

class AuthNamespaceError extends BaseError<AuthNamespaceErrorCode> {
  state: Partial<{
    appDefinitionId: string;
  }> = {};

  constructor(message: string, code: AuthNamespaceErrorCode) {
    super(message, code, 'Auth Strategy Error');
  }

  withAppDefinitionId(appDefinitionId: string) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}

export const createAuthNamespaceError = createErrorBuilder<
  AuthNamespaceErrorCode,
  AuthNamespaceError
>(AuthNamespaceError);

/**
 * https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/wix-api-basics/1002-identities-authentication
 * metasite token
 */
// const META_SITE_APPLICATION_ID = '22bef345-3c5b-4c18-b782-74d4085112ff';
const META_SITE_APPLICATION_ID = '-666';

export class AuthNamespace extends PrivateAPINamespace {
  static readonly namespace = 'auth';

  async #fetchOTT(authInstance: string, payload: ICreateOTTokenRequest) {
    const res = await fetch('/_api/one-time-token/v1/tokens', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        Authorization: authInstance,
      },
    });

    if (!res.ok) {
      throw createAuthNamespaceError(
        AuthNamespaceErrorCode.OTTokenRequestFailed,
      ).withAppDefinitionId(payload.tokenMetadata.appId!);
    }

    const createOTTokenResponse = (await res.json()) as ICreateOTTokenResponse;

    const otp = createOTTokenResponse.tokenResult?.token?.value;

    if (!otp) {
      throw createAuthNamespaceError(
        AuthNamespaceErrorCode.OTTokenRequestFailed,
        'there is no ott value',
      ).withAppDefinitionId(payload.tokenMetadata.appId!);
    }

    return otp;
  }

  async #fetchToken(authInstance: string, payload: ICreateTokenRequest) {
    const res = await fetch('/_api/token-creator/v1/token', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        Authorization: authInstance,
      },
    });

    return (await res.json()) as ICreateTokenResponse;
  }

  async #fetchAccessToken(params: {
    appDefinitionId: string;
    metaSiteId: string;
    originDomain: string;
  }) {
    const authInstance = this.ps.ds.platform.getAppDataByApplicationId(
      META_SITE_APPLICATION_ID,
    )?.instance;

    try {
      const otp = await this.#fetchOTT(authInstance, {
        tokenMetadata: {
          appId: params.appDefinitionId,
          originDomain: params.originDomain,
          siteId: params.metaSiteId,
        },
      });

      const createTokenResponse = await this.#fetchToken(authInstance, {
        otp,
      });

      return createTokenResponse.token;
    } catch (e: any) {
      throw createAuthNamespaceError(
        AuthNamespaceErrorCode.TokenRequestFailed,
      ).withAppDefinitionId(params.appDefinitionId);
    }
  }

  async getAccessToken(
    appDefinitionId: string,
    metaSiteId: string,
  ): Promise<IToken> {
    const originDomain = globalThis.location?.origin ?? '';

    return (await this.#fetchAccessToken({
      appDefinitionId,
      metaSiteId,
      originDomain,
    })) as Promise<IToken>;
  }
}
